import { action, makeObservable, observable, runInAction } from "mobx";
import { injectable, inject } from "inversify";
import type { LayoutFieldType } from "@whatslly/types/LayoutFieldType";
import type { LayoutFieldsSetupBaseNamesType, LayoutFieldsSetupNamesType, LayoutFieldsSetupType } from "@whatslly/types/LayoutFieldsSetupType";
import type { FormPicklistsOptionsType } from "@whatslly/types/FieldPickListOptions";
import isNull from "lodash/isNull";
import upperFirst from "lodash/upperFirst";
import type { GetCustomObjectPicklistsResponseType } from "@whatslly/types/GetCustomObjectPicklistsResponse";
import { getConfigFromLoginProvider } from "@whatslly/commons/login-provider/getConfigFromLoginProvider";
import type { PartialRecord } from "@whatslly/commons/typescriptUtils";
import type { RecordTypeInfo } from "@whatslly/types/SfGetDescribeResponse";
import type { LoginProviderType } from "@whatslly/types/LoginProviderType";
import { FormattedRecordType } from "@whatslly/commons/utilClasses/FormattedRecordType";
import type { IStorage } from "@whatslly/commons/storage.constants";
import { InjectStorage } from "@whatslly/commons/storage.constants";
import type { GetEventFormPicklistsResponseType } from "@whatslly/types/pick-lists-router/GetEventFormPicklistsResponse";
import type { GetFormPicklistsResponseType } from "@whatslly/types/pick-lists-router/GetFormPicklistsResponseType";
import type { GetFormPicklistsResponseContactType } from "@whatslly/types/pick-lists-router/GetFormPicklistsResponseContactType";
import cloneDeep from "lodash/cloneDeep";
import lowerFirst from "lodash/lowerFirst";
import { BgCommands } from "@whatslly/commons/bgCommands";
import { backgroundClient } from "@whatslly/commons/background/client";
import type { GetCustomObjectLookupBodyType } from "@whatslly/types/GetCustomObjectLookupBody";
import type { GetCustomObjectLookupResponseType } from "@whatslly/types/GetCustomObjectLookupResponse";
import type { GetUsersResponseType } from "@whatslly/types/GetUsersResponse";
const getPickListIsInitializedByTypeInitialValue: PartialRecord<LayoutFieldsSetupBaseNamesType, boolean> = {
  newLead: false,
  newContact: false,
  newPersonAccount: false,
  newTask: false,
  newActivity: false,
  newCase: false,
  newEvent: false,
};

const pickListOptionsInitialValue: PartialRecord<LayoutFieldsSetupBaseNamesType, FormPicklistsOptionsType | undefined> = {
  newLead: undefined,
  newContact: undefined,
  newPersonAccount: undefined,
  newAccount: undefined,
  newTask: undefined,
  newActivity: undefined,
  newCase: undefined,
  newEvent: undefined,
};

function createResolveAblePromise(): [Promise<unknown>, resolve: () => void] {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  let resolveToReturn: any;
  const promiseToReturn = new Promise((resolve) => {
    resolveToReturn = resolve;
  });
  return [promiseToReturn, resolveToReturn];
}

@injectable()
export class FieldStore {
  customObjectFields: Map<string, LayoutFieldType[]> = new Map<string, LayoutFieldType[]>();
  newLead?: LayoutFieldType[];
  newContact?: LayoutFieldType[];
  newAccount?: LayoutFieldType[];
  newCase?: LayoutFieldType[];
  newTask?: LayoutFieldType[];
  newActivity?: LayoutFieldType[];
  lead?: LayoutFieldType[];
  contact?: LayoutFieldType[];
  account?: LayoutFieldType[];
  newEvent?: LayoutFieldType[];
  user?: LayoutFieldType[];
  manager?: LayoutFieldType[];
  wapTask?: LayoutFieldType[];
  task?: LayoutFieldType[];
  activity?: LayoutFieldType[];
  case?: LayoutFieldType[];
  event?: LayoutFieldType[];
  storedFields?: LayoutFieldsSetupType;

  opportunity?: LayoutFieldType[];
  isInitialized = false;
  storedRecordTypes: PartialRecord<LayoutFieldsSetupNamesType, RecordTypeInfo[]> = {};
  storedRecordTypesPickListValues: Record<string, FormPicklistsOptionsType | null> = {};
  getPickListIsInitializedByType = cloneDeep(getPickListIsInitializedByTypeInitialValue);
  newPersonAccount?: LayoutFieldType[];
  personAccount?: LayoutFieldType[];

  private pickListsRequestsPromises: PartialRecord<LayoutFieldsSetupNamesType, Promise<unknown>> = {};
  pickListsOptions = { ...pickListOptionsInitialValue };

  constructor(@inject(InjectStorage) private storage: IStorage) {
    makeObservable(this, {
      newLead: observable,
      newContact: observable,
      newAccount: observable,
      newCase: observable,
      newTask: observable,
      newActivity: observable,
      newEvent: observable,
      user: observable,
      manager: observable,
      wapTask: observable,
      task: observable,
      activity: observable,
      case: observable,
      opportunity: observable,
      lead: observable,
      contact: observable,
      account: observable,
      event: observable,
      newPersonAccount: observable,
      personAccount: observable,
      isInitialized: observable,
      storedRecordTypes: observable,
      updateFromQuery: action,
      forceResetData: action,
    });
  }

  updateFromQuery(fields?: LayoutFieldsSetupType) {
    if (!fields) {
      return;
    }
    this.contact = Array.isArray(fields.contact) ? fields.contact : this.contact;
    this.newContact = Array.isArray(fields.newContact) ? fields.newContact : this.newContact;
    this.account = Array.isArray(fields.account) ? fields.account : this.account;
    this.newAccount = Array.isArray(fields.newAccount) ? fields.newAccount : this.newAccount;
    this.lead = Array.isArray(fields.lead) ? fields.lead : this.lead;
    this.newLead = Array.isArray(fields.newLead) ? fields.newLead : this.newLead;
    this.task = Array.isArray(fields.task) ? fields.task : this.task;
    this.newTask = Array.isArray(fields.newTask) ? fields.newTask : this.newTask;
    this.activity = Array.isArray(fields.activity) ? fields.activity : this.activity;
    this.newActivity = Array.isArray(fields.newActivity) ? fields.newActivity : this.newActivity;
    this.manager = Array.isArray(fields.manager) ? fields.manager : this.manager;
    this.user = Array.isArray(fields.user) ? fields.user : this.user;
    this.newEvent = Array.isArray(fields.newEvent) ? fields.newEvent : this.newEvent;
    this.event = Array.isArray(fields.event) ? fields.event : this.event;
    this.wapTask = Array.isArray(fields.wapTask) ? fields.wapTask : this.wapTask;
    this.personAccount = Array.isArray(fields.personAccount) ? fields.personAccount : this.personAccount;
    this.newPersonAccount = Array.isArray(fields.newPersonAccount) ? fields.newPersonAccount : this.newPersonAccount;
  }
  private static getDefaultWapTaskFields(): LayoutFieldType[] {
    return [
      {
        l: "Due Date",
        a: "ActivityDate",
        t: "datetime",
        d: false,
        r: true,
      },
      {
        l: "Priority",
        a: "Priority",
        t: "string",
        d: false,
        r: true,
      },
    ];
  }

  private static getDefaultActivityFields(): LayoutFieldType[] {
    return [
      {
        l: "Due Date",
        a: "ActivityDate",
        t: "datetime",
        d: false,
        r: false,
      },
    ];
  }

  private static getDefaultOpportunityFields(): LayoutFieldType[] {
    return [
      {
        l: `Close Date`,
        a: `CloseDate`,
        t: `datetime`,
        d: false,
        r: false,
      },
      {
        l: "Stage",
        a: "StageName",
        t: "string",
        d: false,
        r: false,
      },
      {
        l: "Amount",
        a: "Amount",
        t: "string",
        d: false,
        r: false,
      },
    ];
  }

  private static getDefaultTaskFields(): LayoutFieldType[] {
    return [
      {
        l: "Due Date",
        a: "ActivityDate",
        t: "datetime",
        d: false,
        r: false,
      },
    ];
  }

  private static getDefaultCaseFields(): LayoutFieldType[] {
    return [
      {
        l: "Status",
        a: "Status",
        t: "string",
        d: false,
        r: false,
      },
    ];
  }

  private static getDefaultManagerFields(): LayoutFieldType[] {
    return [
      {
        l: "Name",
        a: "Name",
        t: "string",
        d: false,
        r: true,
      },
      {
        l: "Email",
        a: "Email",
        t: "email",
        d: false,
        r: true,
      },
      {
        l: "Phone",
        a: "Phone",
        t: "phone",
        d: false,
        r: false,
      },
    ];
  }

  private static getDefaultUserFields(): LayoutFieldType[] {
    return [
      {
        l: "Name",
        a: "Name",
        t: "string",
        d: false,
        r: true,
      },
      {
        l: "Email",
        a: "Email",
        t: "email",
        d: false,
        r: true,
      },
      {
        l: "User Type",
        a: "UserType",
        t: "string",
        d: false,
        r: false,
      },
      {
        l: "Title",
        a: "Title",
        t: "string",
        d: false,
        r: false,
      },
      {
        l: "Phone",
        a: "Phone",
        t: "phone",
        d: false,
        r: false,
      },
      {
        l: "Mobile Phone",
        a: "MobilePhone",
        t: "phone",
        d: false,
        r: false,
      },
      {
        l: "Country",
        a: "Country",
        t: "string",
        d: false,
        r: false,
      },
      {
        l: "City",
        a: "City",
        t: "string",
        d: false,
        r: false,
      },
      {
        l: "Employee Number",
        a: "EmployeeNumber",
        t: "int",
        d: false,
        r: false,
      },
    ];
  }

  private static getDefaultNewEventFields(): LayoutFieldType[] {
    return [
      {
        l: "Subject",
        a: "Subject",
        t: "string",
        r: true,
        d: false,
      },
      {
        l: "Start Time",
        a: "StartDateTime",
        t: "datetime",
        d: false,
        r: true,
      },
      {
        l: "End Time",
        a: "EndDateTime",
        t: "datetime",
        d: false,
        r: true,
      },
      {
        l: "Location",
        a: "Location",
        t: "string",
        d: false,
        r: false,
      },
      {
        l: "Description",
        a: "Description",
        t: "textarea",
        d: false,
        r: false,
      },
      {
        l: "All Day Event",
        a: "IsAllDayEvent",
        t: "boolean",
        d: false,
        r: false,
      },
    ];
  }

  private static getDefaultEventFields(): LayoutFieldType[] {
    return [
      {
        l: "Subject",
        a: "Subject",
        t: "string",
        r: true,
        d: false,
      },
      {
        l: "Start Time",
        a: "StartDateTime",
        t: "datetime",
        d: false,
        r: true,
      },
      {
        l: "End Time",
        a: "EndDateTime",
        t: "datetime",
        d: false,
        r: true,
      },
      {
        l: "Location",
        a: "Location",
        t: "string",
        d: false,
        r: false,
      },
      {
        l: "Description",
        a: "Description",
        t: "textarea",
        d: false,
        r: false,
      },
      {
        l: "All Day Event",
        a: "IsAllDayEvent",
        t: "boolean",
        d: false,
        r: false,
      },
    ];
  }

  private handleSwitchRecordTypesPicklists<T extends Record<string, FormPicklistsOptionsType>>(picklists: T, recordType: string | undefined): T {
    if (!recordType) return picklists;
    const formattedRecordType = FormattedRecordType.formatLeyoutKey(recordType);
    if (formattedRecordType && formattedRecordType.entity in picklists) {
      return {
        ...picklists,
        [formattedRecordType.entity]: this.storedRecordTypesPickListValues[recordType] || [],
      };
    }
    return picklists;
  }

  private recordTypeInCache(recordType: string) {
    const haveRecordTypeInCache = Boolean(this.storedRecordTypesPickListValues[recordType]);
    const recordTypeInCacheIsNull = isNull(this.storedRecordTypesPickListValues[recordType]);
    return haveRecordTypeInCache || recordTypeInCacheIsNull;
  }

  async getCustomObjectPickListFields(
    loginProvider: LoginProviderType,
    customObjectKey: string,
    fields: LayoutFieldsSetupType,
    recordTypeLayoutKey: LayoutFieldsSetupNamesType | undefined,
    selectedPerson: Record<string, any> | undefined
  ): Promise<GetFormPicklistsResponseType> {
    const { urlSuffix } = getConfigFromLoginProvider(loginProvider, `pick-list/customObject/${customObjectKey}`);
    const res = await backgroundClient.send<typeof BgCommands.HTTP_REQUEST, GetCustomObjectPicklistsResponseType>(BgCommands.HTTP_REQUEST, {
      method: "POST",
      urlSuffix,
      body: JSON.stringify({ fields, recordTypeLayoutKey, selectedPerson }),
    });

    if (!res.success) {
      return res;
    }
    return {
      ...res,
      picklistsOptions: res.options,
      recordTypes: res.recordTypes,
    };
  }

  async saveRecordTypesPickListsInCache(
    pickLists: PartialRecord<LayoutFieldsSetupNamesType, FormPicklistsOptionsType>,
    validRecordTypes?: PartialRecord<LayoutFieldsSetupNamesType, RecordTypeInfo[]>,
    recordTypeLayoutKey?: LayoutFieldsSetupNamesType,
    loginProvider?: LoginProviderType
  ) {
    if (!validRecordTypes) return;
    const formattedRecordTypeLayoutKey = recordTypeLayoutKey ? FormattedRecordType.formatLeyoutKey(recordTypeLayoutKey) : undefined;
    const fields = await this.getStorageFields(loginProvider);
    const layoutKeys = Object.keys(fields) as LayoutFieldsSetupNamesType[];
    const selectedEntities = Object.keys(validRecordTypes) as LayoutFieldsSetupNamesType[];
    const defaultRecordTypes = selectedEntities.map((entityKey) =>
      layoutKeys.find((layoutKey) => {
        const defaultLayoutKey = layoutKey.startsWith(entityKey) && layoutKey.includes(":default");
        const isSelectedRecordTypeEntity = formattedRecordTypeLayoutKey?.entity && layoutKey.startsWith(formattedRecordTypeLayoutKey?.entity);
        const isSelectedRecordType = recordTypeLayoutKey === layoutKey;
        return Boolean(isSelectedRecordType) || (defaultLayoutKey && !isSelectedRecordTypeEntity);
      })
    );
    const filteredDefaultRecordType = defaultRecordTypes.filter(Boolean) as LayoutFieldsSetupNamesType[];
    const formattedDefaultRecordTypes = filteredDefaultRecordType.map((recordType) => FormattedRecordType.formatLeyoutKey(recordType));
    const formattedDefaultRecordTypesToUse = formattedDefaultRecordTypes[0]
      ? formattedDefaultRecordTypes
      : formattedRecordTypeLayoutKey
        ? [formattedRecordTypeLayoutKey]
        : [];
    const recordTypesToCachePickListsEntries = formattedDefaultRecordTypesToUse.map((formattedDefaultRecordType) => [
      FormattedRecordType.formattedLayoutKeyToString(formattedDefaultRecordType),
      pickLists[formattedDefaultRecordType.entity] || null,
    ]);
    const recordTypesPickListsToCache = Object.fromEntries(recordTypesToCachePickListsEntries);
    this.storedRecordTypesPickListValues = { ...this.storedRecordTypesPickListValues, ...recordTypesPickListsToCache };
  }

  private async buildGenericResToGetPickLists(
    layoutKey: LayoutFieldsSetupBaseNamesType,
    loginProvider: LoginProviderType,
    recordTypeLayoutKey?: LayoutFieldsSetupNamesType,
    isCustomObject?: boolean,
    selectedPerson?: Record<string, any>
  ) {
    const sfdcSettings = await this.getStorageFields(loginProvider);
    const entityToUrl = lowerFirst(layoutKey.startsWith("new") ? layoutKey.replace("new", "") : layoutKey);
    const { urlSuffix } = getConfigFromLoginProvider(loginProvider, `pick-list/fields/${entityToUrl}`);
    // const person = this.personStore.person && !Array.isArray(this.personStore.person) ? this.personStore.person : undefined;
    const res = isCustomObject
      ? await this.getCustomObjectPickListFields(loginProvider, entityToUrl, sfdcSettings, recordTypeLayoutKey, selectedPerson)
      : await backgroundClient.send<typeof BgCommands.HTTP_REQUEST, GetFormPicklistsResponseType>(BgCommands.HTTP_REQUEST, {
          method: "POST",
          body: JSON.stringify({ fields: sfdcSettings, recordTypeLayoutKey }),
          urlSuffix,
        });
    if (!res.success) {
      return {
        success: false as const,
        error: res.error,
      };
    }

    const resToReturn: {
      success: true;
      pickListsOptions: FormPicklistsOptionsType;
      recordTypes?: RecordTypeInfo[];
      accountPickListsOptions: FormPicklistsOptionsType | undefined;
      accountRecordType?: RecordTypeInfo[];
    } = {
      success: true as const,
      pickListsOptions: res.picklistsOptions,
      recordTypes: res.recordTypes,
      accountPickListsOptions: undefined,
    };

    if (layoutKey === "newEvent") {
      const newEventRes = res as unknown as GetEventFormPicklistsResponseType;
      if (newEventRes.success) {
        resToReturn.pickListsOptions = newEventRes.newEvent;
        resToReturn.recordTypes = newEventRes.recordTypes?.newEvent;
      }
    }

    if (layoutKey === "newContact") {
      const newContactRes = res as GetFormPicklistsResponseContactType;
      if (!newContactRes.success)
        return {
          success: false as const,
          error: newContactRes.error,
        };
      resToReturn["accountPickListsOptions"] = newContactRes.accountPickListsOptions;
      resToReturn["accountRecordType"] = newContactRes.accountRecordType;
    }
    return resToReturn;
  }

  async getCreateEntityPickLists(
    loginProvider: LoginProviderType,
    layoutKey: LayoutFieldsSetupBaseNamesType,
    recordTypeLayoutKey: LayoutFieldsSetupNamesType | undefined,
    isCustomObject: boolean | undefined,
    selectedPerson?: Record<string, any>
  ): Promise<FormPicklistsOptionsType> {
    const layoutKeyToUse = layoutKey === "newAccount" ? "newContact" : layoutKey;
    if (this.pickListsRequestsPromises[layoutKeyToUse]) await this.pickListsRequestsPromises[layoutKeyToUse];

    if (!this.getPickListIsInitializedByType[layoutKeyToUse] || (recordTypeLayoutKey && !this.recordTypeInCache(recordTypeLayoutKey))) {
      const [leadPickListPromise, resolvePromise] = createResolveAblePromise();
      this.pickListsRequestsPromises[layoutKeyToUse] = leadPickListPromise;
      const res = await this.buildGenericResToGetPickLists(layoutKeyToUse, loginProvider, recordTypeLayoutKey, isCustomObject, selectedPerson);
      if (res.success) {
        this.pickListsOptions[layoutKeyToUse] = res.pickListsOptions;
        this.getPickListIsInitializedByType[layoutKeyToUse] = true;
        this.storedRecordTypes[layoutKey] = res.recordTypes;
        const picklistsToCache: PartialRecord<LayoutFieldsSetupBaseNamesType, FormPicklistsOptionsType | undefined> = {
          [layoutKeyToUse]: res.pickListsOptions,
        };
        const recordTypesToCache: PartialRecord<LayoutFieldsSetupBaseNamesType, RecordTypeInfo[] | undefined> = {
          [layoutKeyToUse]: res.recordTypes,
        };
        if (layoutKeyToUse === "newContact") {
          this.pickListsOptions.newAccount = res.accountPickListsOptions;
          picklistsToCache["newAccount"] = res.accountPickListsOptions;
          recordTypesToCache["newAccount"] = res.accountRecordType;
          this.storedRecordTypes["newAccount"] = res.accountRecordType;
        }
        await this.saveRecordTypesPickListsInCache(picklistsToCache, recordTypesToCache, recordTypeLayoutKey, loginProvider);
      }
      resolvePromise();
    }

    return this.handleSwitchRecordTypesPicklists(
      {
        [layoutKey]: this.pickListsOptions[layoutKey] ?? [],
      },
      recordTypeLayoutKey
    )[layoutKey];
  }

  forceResetData() {
    this.getPickListIsInitializedByType = cloneDeep(getPickListIsInitializedByTypeInitialValue);
    this.pickListsRequestsPromises = {};
    this.storedRecordTypesPickListValues = {};
    this.isInitialized = false;
  }

  async getCustomObjectFields(customObjectKey: string, loginProvider: LoginProviderType): Promise<LayoutFieldType[]> {
    const fieldsStorage = await this.getStorageFields(loginProvider);
    const layoutKey = `new${upperFirst(customObjectKey)}`;
    const fields = (fieldsStorage as unknown as Record<string, LayoutFieldType[]>)[layoutKey];
    this.customObjectFields.set(layoutKey, fields ?? []);

    return fields ?? [];
  }

  async getStorageFields(loginProvider: LoginProviderType) {
    const { urlSuffix } = getConfigFromLoginProvider(loginProvider, `fields`);
    const res = await backgroundClient.send<typeof BgCommands.HTTP_REQUEST, any>(BgCommands.HTTP_REQUEST, {
      method: "GET",
      urlSuffix,
    });
    return res.fields ?? ({} as LayoutFieldsSetupType);
  }

  async getFields(loginProvider: LoginProviderType): Promise<LayoutFieldsSetupType> {
    if (!this.isInitialized) {
      const fields = await this.getStorageFields(loginProvider);
      runInAction(() => {
        this.storedFields = fields;
        this.newLead = fields.newLead;
        this.newContact = fields.newContact;
        this.newAccount = fields.newAccount;
        this.newCase = fields.newCase;
        this.newTask = fields.newTask;
        this.newActivity = fields.newActivity;
        this.lead = fields.lead;
        this.contact = fields.contact;
        this.account = fields.account;
        this.newEvent = fields.newEvent;
        this.user = fields.user;
        this.manager = fields.manager;
        this.wapTask = fields.wapTask;
        this.task = fields.task;
        this.activity = fields.activity;
        this.case = fields.case;
        this.isInitialized = true;
        this.opportunity = fields.opportunity;
        this.newPersonAccount = fields.newPersonAccount;
        this.personAccount = fields.personAccount;
        this.event = fields.event;
      });
    }
    return {
      user: Array.isArray(this.user) && this.user.length > 0 ? this.user : FieldStore.getDefaultUserFields(),
      manager: Array.isArray(this.manager) && this.manager.length > 0 ? this.manager : FieldStore.getDefaultManagerFields(),
      wapTask: Array.isArray(this.wapTask) && this.wapTask.length > 0 ? this.wapTask : FieldStore.getDefaultWapTaskFields(),
      task: Array.isArray(this.task) && this.task.length > 0 ? this.task : FieldStore.getDefaultTaskFields(),
      activity: Array.isArray(this.activity) && this.activity.length > 0 ? this.activity : FieldStore.getDefaultActivityFields(),
      case: Array.isArray(this.case) && this.case.length > 0 ? this.case : FieldStore.getDefaultCaseFields(),
      opportunity: Array.isArray(this.opportunity) && this.opportunity.length > 0 ? this.opportunity : FieldStore.getDefaultOpportunityFields(),
      newLead: this.newLead ?? [],
      newContact: this.newContact ?? [],
      newAccount: (this.newAccount ?? []).map((filed) => {
        //TEMP fix change name type to string
        // Hey Ron, could we delete this alrady?
        if (filed.a === "Name") {
          return { ...filed, t: "string", ro: true };
        }
        return filed;
      }),
      newCase: this.newCase ?? [],
      newTask: this.newTask ?? [],
      newActivity: this.newActivity ?? [],
      lead: this.lead ?? [],
      contact: this.contact ?? [],
      account: this.account ?? [],
      newEvent: Array.isArray(this.newEvent) && this.newEvent.length > 0 ? this.newEvent : FieldStore.getDefaultNewEventFields(),
      event: Array.isArray(this.event) && this.event.length > 0 ? this.event : FieldStore.getDefaultEventFields(),
      newPersonAccount: this.newPersonAccount ?? (this.newContact || []),
      personAccount: this.personAccount ?? [...(this.contact || []), ...(this.account || [])],
      ...this.storedFields,
    };
  }

  static lookupFields(
    loginProvider: LoginProviderType,
    objectApiName: string,
    body: GetCustomObjectLookupBodyType,
    selectedPersonId?: string,
    selectedPersonType?: string,
    selectedAccountId?: string
  ): Promise<GetCustomObjectLookupResponseType> {
    const { urlSuffix } = getConfigFromLoginProvider(loginProvider, `customObject/lookup/${objectApiName}`);
    return backgroundClient.send<typeof BgCommands.HTTP_REQUEST, GetCustomObjectLookupResponseType>(BgCommands.HTTP_REQUEST, {
      method: "POST",
      urlSuffix,
      body: JSON.stringify({ ...body, selectedPersonId, selectedPersonType, selectedAccountId }),
    });
  }

  static listUsers(loginProvider: LoginProviderType, search: string): Promise<GetUsersResponseType> {
    const { urlSuffix } = getConfigFromLoginProvider(loginProvider, `users?search=${search}`);
    return backgroundClient.send<typeof BgCommands.HTTP_REQUEST, GetUsersResponseType>(BgCommands.HTTP_REQUEST, { method: "GET", urlSuffix });
  }
}
